export interface ScheduleExportSource {
    metrics: {
        completion_rate: number;
        coverage_rate: number;
        satisfaction?: number;
        solve_time: number;
    };
    details: {
        total_positions: number;
        assigned_positions: number;
        total_operations: number;
        covered_operations: number;
    };
    assignments: any[];
    shift_assignments?: any[];
    operations?: any[];
    calendar_days?: { date: string; is_workday: boolean }[];
    employee_meta?: Record<number, {
        name?: string;
        department?: string | null;
    }>;
    standard_hours?: number;
    run_status?: string;
}

export interface ScheduleExportPosition {
    operationId: number;
    plannedStart: string;
    plannedEnd: string;
    rosterDate: string;
    shiftId: number | null;
    batchCode: string;
    stageName: string;
    operationName: string;
    positionNumber: number;
    employeeId: number | null;
    employeeName: string;
    employeeCode: string;
    employeeDepartment: string;
    assigned: boolean;
    qualifications: string;
    taskType: string;
    durationMinutes: number;
    windowStart: string;
    windowEnd: string;
    shareGroupName: string;
    locked: boolean;
    eligibleEmployeeCount: number | null;
}

export interface ScheduleExportCoverage {
    totalPositions: number;
    assignedPositions: number;
    totalOperations: number;
    coveredOperations: number;
    completedOperations: number;
    completionRate: number;
    coverageRate: number;
}

export interface ScheduleExportProjection {
    positions: ScheduleExportPosition[];
    gaps: ScheduleExportPosition[];
    coverage: ScheduleExportCoverage;
}

const normalizeRate = (value: unknown): number => {
    const numeric = Number(value) || 0;
    return numeric > 1 ? numeric / 100 : numeric;
};

export const formatScheduleQualifications = (requirements: any[]): string => {
    if (!Array.isArray(requirements) || requirements.length === 0) return '-';
    return requirements.map((requirement) => {
        const name = requirement.qualification_name || `资质${requirement.qualification_id || ''}`;
        const level = Number(requirement.required_level);
        const levelText = Number.isFinite(level) ? ` ≥${level}级` : '';
        return `${name}${levelText}${requirement.is_mandatory === false ? '（可选）' : ''}`;
    }).join('；');
};

/**
 * Canonical projection shared by every V4 export.
 *
 * The editable source of truth is `operations[].positions`; immutable solver
 * assignments are only a legacy fallback for historical runs without position
 * details.
 */
export const buildScheduleExportProjection = (
    data: ScheduleExportSource,
): ScheduleExportProjection => {
    const operations = Array.isArray(data.operations) ? data.operations : [];
    const hasPositionDetails = operations.some((operation) => (
        Array.isArray(operation.positions) && operation.positions.length > 0
    ));
    const positions: ScheduleExportPosition[] = [];

    if (hasPositionDetails) {
        operations.forEach((operation) => {
            (operation.positions || []).forEach((position: any) => {
                const employee = position.employee || null;
                const assigned = Boolean(employee?.id || employee?.name)
                    && String(position.status || 'ASSIGNED').toUpperCase() === 'ASSIGNED';
                const executionStart = assigned
                    ? position.assignment_start || operation.planned_start || ''
                    : operation.planned_start || '';
                const executionEnd = assigned
                    ? position.assignment_end || operation.planned_end || ''
                    : operation.planned_end || '';
                const employeeId = assigned && Number.isFinite(Number(employee?.id))
                    ? Number(employee.id)
                    : null;
                positions.push({
                    operationId: Number(operation.operation_plan_id || 0),
                    plannedStart: executionStart,
                    plannedEnd: executionEnd,
                    rosterDate: assigned
                        ? String(position.roster_date || executionStart).slice(0, 10)
                        : String(operation.planned_start || '').slice(0, 10),
                    shiftId: assigned && position.shift_id != null
                        ? Number(position.shift_id)
                        : null,
                    batchCode: operation.batch_code || '',
                    stageName: operation.stage_name || '-',
                    operationName: operation.operation_name || '',
                    positionNumber: Number(position.position_number) || 1,
                    employeeId,
                    employeeName: assigned ? employee?.name || '-' : '-',
                    employeeCode: assigned ? employee?.code || '-' : '-',
                    employeeDepartment: employeeId == null
                        ? '-'
                        : data.employee_meta?.[employeeId]?.department || '-',
                    assigned,
                    qualifications: formatScheduleQualifications(position.qualification_requirements),
                    taskType: operation.task_type || '',
                    durationMinutes: Number(operation.duration_minutes) || 0,
                    windowStart: operation.window_start || operation.planned_start || '',
                    windowEnd: operation.window_end || operation.planned_end || '',
                    shareGroupName: operation.share_group_name || '',
                    locked: Boolean(position.is_locked),
                    eligibleEmployeeCount: Array.isArray(position.eligible_employee_ids)
                        ? position.eligible_employee_ids.length
                        : null,
                });
            });
        });
    } else {
        (data.assignments || []).forEach((assignment) => {
            const assigned = Boolean(assignment.employee_id || assignment.employee_name)
                && assignment.employee_name !== '-';
            positions.push({
                operationId: Number(assignment.operation_plan_id || assignment.operation_id || 0),
                plannedStart: assignment.planned_start || '',
                plannedEnd: assignment.planned_end || '',
                rosterDate: String(assignment.date || assignment.planned_start || '').slice(0, 10),
                shiftId: assignment.shift_id == null ? null : Number(assignment.shift_id),
                batchCode: assignment.batch_code || '',
                stageName: assignment.stage_name || '-',
                operationName: assignment.operation_name || '',
                positionNumber: Number(assignment.position_number) || 1,
                employeeId: assigned && Number.isFinite(Number(assignment.employee_id))
                    ? Number(assignment.employee_id)
                    : null,
                employeeName: assigned ? assignment.employee_name || '-' : '-',
                employeeCode: assigned ? assignment.employee_code || '-' : '-',
                employeeDepartment: assigned
                    ? data.employee_meta?.[Number(assignment.employee_id)]?.department || '-'
                    : '-',
                assigned,
                qualifications: assignment.qualification_name || '-',
                taskType: assignment.task_type || '',
                durationMinutes: Number(assignment.duration_minutes) || 0,
                windowStart: assignment.window_start || assignment.planned_start || '',
                windowEnd: assignment.window_end || assignment.planned_end || '',
                shareGroupName: assignment.share_group_name || '',
                locked: Boolean(assignment.is_locked),
                eligibleEmployeeCount: Array.isArray(assignment.eligible_employee_ids)
                    ? assignment.eligible_employee_ids.length
                    : null,
            });
        });
    }

    positions.sort((left, right) => {
        const timeOrder = left.plannedStart.localeCompare(right.plannedStart);
        if (timeOrder !== 0) return timeOrder;
        const batchOrder = left.batchCode.localeCompare(right.batchCode);
        if (batchOrder !== 0) return batchOrder;
        const operationOrder = left.operationId - right.operationId;
        return operationOrder || left.positionNumber - right.positionNumber;
    });

    const operationGroups = new Map<number, ScheduleExportPosition[]>();
    positions.forEach((position) => {
        const key = position.operationId || Number.MAX_SAFE_INTEGER - operationGroups.size;
        if (!operationGroups.has(key)) operationGroups.set(key, []);
        operationGroups.get(key)!.push(position);
    });
    const totalOperations = hasPositionDetails
        ? operations.length
        : Number(data.details.total_operations) || operationGroups.size;
    const coveredOperations = hasPositionDetails
        ? operations.filter((operation) => (
            (operation.positions || []).some((position: any) => (
                position.status === 'ASSIGNED' && position.employee
            ))
        )).length
        : Number(data.details.covered_operations) || 0;
    const completedOperations = hasPositionDetails
        ? operations.filter((operation) => (
            (operation.positions || []).length > 0
            && operation.positions.every((position: any) => (
                position.status === 'ASSIGNED' && position.employee
            ))
        )).length
        : coveredOperations;
    const totalPositions = positions.length || Number(data.details.total_positions) || 0;
    const assignedPositions = positions.length
        ? positions.filter((position) => position.assigned).length
        : Number(data.details.assigned_positions) || 0;

    return {
        positions,
        gaps: positions.filter((position) => !position.assigned),
        coverage: {
            totalPositions,
            assignedPositions,
            totalOperations,
            coveredOperations,
            completedOperations,
            completionRate: totalPositions > 0
                ? assignedPositions / totalPositions
                : normalizeRate(data.metrics.completion_rate),
            coverageRate: totalOperations > 0
                ? coveredOperations / totalOperations
                : normalizeRate(data.metrics.coverage_rate),
        },
    };
};
